import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { HeroBackground } from "@/components/site/HeroBackground";
import { serviceCategories } from "@/data/site/services";
import { stockImages } from "@/data/site/stockImages";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

const ServicesIndex = () => (
  <SiteLayout>
    <PageSEO
      title="Tax Resolution Services"
      description="CPA-led IRS tax resolution services: tax debt relief, collections defense, audit representation, and business tax resolution. Free confidential consultation."
      path="/services"
      image={stockImages.taxFormsCalculator.src}
    />

    <section className="relative bg-navy-dark text-white overflow-hidden">
      <HeroBackground image={stockImages.taxFormsCalculator} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
        <Reveal className="max-w-3xl">
          <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Services" }]} tone="dark" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mt-2 mb-4">
            What we resolve
          </p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
            Every IRS problem has a procedure.{" "}
            <span className="text-gold">We know them all.</span>
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            Tax resolution isn't magic — it's a set of specific IRS programs with specific
            qualification rules. The difference between a great outcome and an expensive mistake is
            knowing which program fits your facts.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-16 lg:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-6xl space-y-20 lg:space-y-28">
        {serviceCategories.map((cat) => (
          <div key={cat.slug}>
            <Reveal className="max-w-2xl mb-10">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
                Category
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-[1.15] [text-wrap:balance] mb-3">
                <Link to={`/services/${cat.slug}`} className="hover:text-gold">
                  {cat.title}
                </Link>
              </h2>
              <p className="text-foreground/70 text-lg leading-relaxed">{cat.tagline}</p>
            </Reveal>
            <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
              {cat.services.map((s) => (
                <RevealItem key={s.slug}>
                  <Link
                    to={`/services/${cat.slug}/${s.slug}`}
                    className="group block bg-white p-7 hover:bg-secondary transition-colors h-full"
                  >
                    <h3 className="font-heading font-bold text-navy text-base group-hover:text-gold mb-2">
                      {s.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
                  </Link>
                </RevealItem>
              ))}
            </RevealStagger>
            <Reveal className="mt-6">
              <Link
                to={`/services/${cat.slug}`}
                className="text-sm font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
              >
                Category overview <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        ))}
      </div>
    </section>

    <CTASection />
  </SiteLayout>
);

export default ServicesIndex;
